// @mui
import {
  Paper,
  Stack,
  Dialog,
  Button,
  DialogTitle,
  DialogActions,
  DialogContent,
  FormHelperText,
} from '@mui/material';
import { DatePicker, DateCalendar } from '@mui/x-date-pickers';
// hooks
import useResponsive from '../../hooks/useResponsive';
//
import { DateRangePickerProps } from './types';
import { useEffect, useState } from 'react';

// ----------------------------------------------------------------------

export default function DateRangePicker({
  title = 'Select date range',
  variant = 'input',
  //
  startDate,
  endDate,
  //
  onChangeStartDate,
  onChangeEndDate,
  onChangeDateRange,
  //
  open,
  onClose,
}: DateRangePickerProps) {
  const isDesktop = useResponsive('up', 'md');
  const [internalStartDate, setInternalStartDate] = useState<Date | null>(startDate);
  const [internalEndDate, setInternalEndDate] = useState<Date | null>(endDate);
  const [errorText, setErrorText] = useState<string>('');

  useEffect(() => {
    setInternalStartDate(startDate);
    setInternalEndDate(endDate);
  }, [startDate, endDate, open]);

  useEffect(() => {
    if (!internalStartDate) {
      setErrorText('Start date is required');
    } else if (!internalEndDate) {
      setErrorText('End date is required');
    } else if (internalStartDate > internalEndDate) {
      setErrorText('End date must be later than start date');
    } else {
      setErrorText('');
    }
  }, [internalStartDate, internalEndDate]);

  const isCalendarView = variant === 'calendar';

  const handleUpdateDateRange = () => {
    if (onChangeStartDate) {
      onChangeStartDate(internalStartDate);
    }
    if (onChangeEndDate) {
      onChangeEndDate(internalEndDate);
    }
    if (onChangeDateRange) {
      onChangeDateRange([internalStartDate, internalEndDate]);
    }
    onClose();
  };

  return (
    <Dialog
      fullWidth
      maxWidth={isCalendarView ? false : 'xs'}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          ...(isCalendarView && {
            maxWidth: 720,
          }),
        },
      }}
    >
      <DialogTitle sx={{ pb: 2 }}>{title}</DialogTitle>

      <DialogContent
        sx={{
          ...(isCalendarView &&
            isDesktop && {
              overflow: 'unset',
            }),
        }}
      >
        <Stack
          spacing={isCalendarView ? 3 : 2}
          direction={isCalendarView && isDesktop ? 'row' : 'column'}
          justifyContent="center"
          sx={{
            pt: 1,
            '& .MuiCalendarPicker-root': {
              ...(!isDesktop && {
                width: 'auto',
              }),
            },
          }}
        >
          {isCalendarView ? (
            <>
              <Paper
                variant="outlined"
                sx={{ borderRadius: 2, borderColor: 'divider', borderStyle: 'dashed' }}
              >
                <DateCalendar
                  value={internalStartDate}
                  onChange={(date) => setInternalStartDate(date)}
                  maxDate={internalEndDate || undefined}
                />
              </Paper>

              <Paper
                variant="outlined"
                sx={{ borderRadius: 2, borderColor: 'divider', borderStyle: 'dashed' }}
              >
                <DateCalendar
                  value={internalEndDate}
                  onChange={(date) => setInternalEndDate(date)}
                  minDate={internalStartDate || undefined}
                />
              </Paper>
            </>
          ) : (
            <>
              <DatePicker
                label="Start date"
                value={internalStartDate}
                onChange={(date) => setInternalStartDate(date)}
                maxDate={internalEndDate || undefined}
              />

              <DatePicker
                label="End date"
                value={internalEndDate}
                onChange={(date) => setInternalEndDate(date)}
                minDate={internalStartDate || undefined}
              />
            </>
          )}
        </Stack>

        {!!errorText && (
          <FormHelperText sx={{ color: 'error.main', px: 2 }}>{errorText}</FormHelperText>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={onClose}>
          Cancel
        </Button>

        <Button disabled={!!errorText} variant="contained" onClick={handleUpdateDateRange}>
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}
